import { useRef, useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { BoundedDate } from "../boundedDate";
import { DateDigitMask } from "../dateDigitMask";
import { cx } from "../cx";
import type { FillMark } from "../extractFill";
import { useQuoteCopy } from "../i18n/QuoteCopyContext";
import { AlertIcon, CalendarIcon, CheckIcon, CloseIcon, SparklesIcon } from "./icons";

function isSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome|Chromium|Android/.test(ua);
}

class DatePickerCloser {
  close(input: HTMLInputElement): void {
    input.blur();
  }
}

const datePickerCloser = new DatePickerCloser();

export function FormPageAlert({ show }: { show: boolean }) {
  const copy = useQuoteCopy();
  if (!show) return null;
  return (
    <div className="nv-form-error" role="alert">
      <AlertIcon size={16} />
      <span>{copy.t("alert.completeFields")}</span>
    </div>
  );
}

export function FillBadge({ mark }: { mark: FillMark }) {
  const copy = useQuoteCopy();
  if (mark === "filled") {
    return (
      <span className="nv-fill-badge filled">
        <SparklesIcon size={12} />
        {copy.t("fill.filled")}
      </span>
    );
  }
  return (
    <span className="nv-fill-badge missing">
      <AlertIcon size={12} />
      {copy.t("fill.notFound")}
    </span>
  );
}

// Shared label + control + inline error wrapper; `id` is the wrapped control's id.
interface FieldShellProps {
  id: string;
  label?: ReactNode;
  required?: boolean;
  error?: string;
  hint?: ReactNode;
  className?: string;
  fillStatus?: FillMark;
  children: ReactNode;
}

export function FieldShell({
  id,
  label,
  required,
  error,
  hint,
  className,
  fillStatus,
  children,
}: FieldShellProps) {
  return (
    <div className={cx("nv-field", error && "err", fillStatus && `nv-mark-${fillStatus}`, className)}>
      {label !== undefined && (
        <div className="nv-label-row">
          <label className="nv-label" htmlFor={id}>
            {label}
            {required && (
              <span className="nv-req" aria-hidden="true">
                {"\u00A0*"}
              </span>
            )}
          </label>
          {fillStatus && <FillBadge mark={fillStatus} />}
        </div>
      )}
      {children}
      {hint ? (
        <p className="nv-field-hint nv-field-hint-inline" id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
      {error && (
        <span className="nv-error" id={`${id}-err`}>
          <AlertIcon size={13} />
          <span>{error}</span>
        </span>
      )}
    </div>
  );
}

type NativeInput = Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "id" | "value">;

interface TextInputProps extends NativeInput {
  id: string;
  label?: ReactNode;
  required?: boolean;
  error?: string;
  hint?: ReactNode;
  className?: string;
  suffix?: string;
  fillStatus?: FillMark;
  value: string;
  onChange: (value: string) => void;
}

interface DateInputProps {
  id: string;
  value: string;
  required?: boolean;
  error?: string;
  describedBy?: string;
  placeholder?: string;
  min?: string | number;
  max?: string | number;
  inputRest: Omit<NativeInput, "min" | "max" | "type" | "placeholder">;
  onChange: (value: string) => void;
}

class DateTypedChange {
  constructor(
    private readonly setDraft: (text: string) => void,
    private readonly onChange: (value: string) => void,
    private readonly mask = new DateDigitMask(),
  ) {}

  handle(text: string, previous: string): void {
    const masked = this.mask.format(text, previous);
    this.setDraft(masked);
    const parsed = BoundedDate.parseTyped(masked);
    if (parsed !== null) {
      this.onChange(parsed);
      return;
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(masked)) this.onChange(masked);
  }
}

class DateTypedBlur {
  constructor(
    private readonly setDraft: (value: string | null) => void,
    private readonly onChange: (value: string) => void,
  ) {}

  handle(text: string): void {
    const parsed = BoundedDate.parseTyped(text);
    this.onChange(parsed !== null ? parsed : text);
    this.setDraft(null);
  }
}

class DatePickerChange {
  constructor(
    private readonly pickedRaw: { current: string },
    private readonly setDraft: (value: string | null) => void,
    private readonly onChange: (value: string) => void,
    private readonly closePicker: (remount: boolean) => void,
  ) {}

  handle(changeRaw: string, target: HTMLInputElement, min: string, max: string): void {
    const raw = BoundedDate.chosen(this.pickedRaw.current, changeRaw, min);
    this.pickedRaw.current = "";
    const next = BoundedDate.capInput(raw, min, max);
    target.value = next;
    this.setDraft(null);
    this.onChange(next);
    if (BoundedDate.isCalendarDate(next)) this.closePicker(true);
  }
}

function DateInput({
  id,
  value,
  required,
  error,
  describedBy,
  placeholder,
  min,
  max,
  inputRest,
  onChange,
}: DateInputProps) {
  const copy = useQuoteCopy();
  const pickerRef = useRef<HTMLInputElement>(null);
  const pickedRaw = useRef("");
  const [safariPickerKey, setSafariPickerKey] = useState(0);
  const [draft, setDraft] = useState<string | null>(null);
  const datePlaceholder = placeholder || copy.t("route.datePlaceholder");
  const { onBlur, onKeyDown, ...restInput } = inputRest;
  const shown = draft !== null ? draft : BoundedDate.shown(value);
  const pickerValue = BoundedDate.isCalendarDate(value) ? value : "";
  const typedChange = new DateTypedChange(setDraft, onChange);
  const typedBlur = new DateTypedBlur(setDraft, onChange);

  const closePicker = (remount: boolean) => {
    if (pickerRef.current) datePickerCloser.close(pickerRef.current);
    if (remount && isSafari()) setSafariPickerKey((n) => n + 1);
  };

  const pickerChange = new DatePickerChange(pickedRaw, setDraft, onChange, closePicker);

  return (
    <div className="nv-date-wrap">
      <input
        id={id}
        {...restInput}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        maxLength={10}
        value={shown}
        placeholder={datePlaceholder}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy}
        onChange={(e) => typedChange.handle(e.target.value, draft ?? shown)}
        onFocus={() => setDraft(BoundedDate.shown(value) || "")}
        onBlur={(e) => {
          typedBlur.handle(draft ?? e.target.value);
          onBlur?.(e);
        }}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          if (e.key === "Enter") e.preventDefault();
          if (e.ctrlKey || e.metaKey || e.altKey) return;
          if (e.key.length === 1 && !/^\d$/.test(e.key)) e.preventDefault();
        }}
      />
      <button
        type="button"
        className="nv-date-action"
        aria-label={value ? copy.t("date.clear") : copy.t("date.closeCalendar")}
        onClick={() => {
          setDraft("");
          if (value) onChange("");
          closePicker(true);
        }}
      >
        <CloseIcon size={14} />
      </button>
      <span className="nv-date-action nv-date-calendar">
        <input
          key={safariPickerKey}
          ref={pickerRef}
          className="nv-date-native"
          type="date"
          aria-label={copy.t("date.openCalendar")}
          value={pickerValue}
          min={isSafari() ? undefined : min}
          max={max}
          onInput={(e) => {
            pickedRaw.current = e.currentTarget.value;
          }}
          onChange={(e) =>
            pickerChange.handle(e.target.value, e.target, String(min ?? ""), String(max ?? ""))
          }
        />
        <CalendarIcon size={16} />
      </span>
    </div>
  );
}

export function TextInput({
  id,
  label,
  required,
  error,
  hint,
  className,
  suffix,
  fillStatus,
  value,
  onChange,
  ...rest
}: TextInputProps) {
  const describedBy = [error ? `${id}-err` : "", hint ? `${id}-hint` : ""]
    .filter(Boolean)
    .join(" ") || undefined;
  const { min, max, type, placeholder, ...inputRest } = rest;

  if (type === "date") {
    return (
      <FieldShell
        id={id}
        label={label}
        required={required}
        error={error}
        hint={hint}
        className={className}
        fillStatus={fillStatus}
      >
        <DateInput
          id={id}
          value={value}
          required={required}
          error={error}
          describedBy={describedBy}
          placeholder={placeholder}
          min={min}
          max={max}
          inputRest={inputRest}
          onChange={onChange}
        />
      </FieldShell>
    );
  }

  const input = (
    <input
      id={id}
      value={value}
      aria-invalid={error ? true : undefined}
      aria-required={required || undefined}
      aria-describedby={describedBy}
      placeholder={placeholder}
      {...inputRest}
      type={type}
      min={min}
      max={max}
      onChange={(e) => onChange(e.target.value)}
    />
  );

  return (
    <FieldShell
      id={id}
      label={label}
      required={required}
      error={error}
      hint={hint}
      className={className}
      fillStatus={fillStatus}
    >
      {suffix ? (
        <div className="nv-suffix-wrap">
          {input}
          <span className="nv-suffix-unit" aria-hidden="true">{suffix}</span>
        </div>
      ) : (
        input
      )}
    </FieldShell>
  );
}

type NativeSelect = Omit<SelectHTMLAttributes<HTMLSelectElement>, "onChange" | "id" | "value">;

interface SelectInputProps extends NativeSelect {
  id: string;
  label?: ReactNode;
  required?: boolean;
  error?: string;
  className?: string;
  fillStatus?: FillMark;
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
}

export function SelectInput({
  id,
  label,
  required,
  error,
  className,
  fillStatus,
  value,
  onChange,
  children,
  ...rest
}: SelectInputProps) {
  return (
    <FieldShell
      id={id}
      label={label}
      required={required}
      error={error}
      className={className}
      fillStatus={fillStatus}
    >
      <div className="nv-select-wrap">
        <select
          id={id}
          value={value}
          aria-invalid={error ? true : undefined}
          aria-required={required || undefined}
          aria-describedby={error ? `${id}-err` : undefined}
          onChange={(e) => onChange(e.target.value)}
          {...rest}
        >
          {children}
        </select>
      </div>
    </FieldShell>
  );
}

type NativeTextArea = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange" | "id" | "value">;

interface TextAreaProps extends NativeTextArea {
  id: string;
  label?: ReactNode;
  required?: boolean;
  error?: string;
  className?: string;
  fillStatus?: FillMark;
  value: string;
  onChange: (value: string) => void;
}

export function TextArea({
  id,
  label,
  required,
  error,
  className,
  fillStatus,
  value,
  onChange,
  ...rest
}: TextAreaProps) {
  return (
    <FieldShell
      id={id}
      label={label}
      required={required}
      error={error}
      className={className}
      fillStatus={fillStatus}
    >
      <textarea
        id={id}
        value={value}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={error ? `${id}-err` : undefined}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
      />
    </FieldShell>
  );
}

interface CheckboxProps {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  className?: string;
  fillStatus?: FillMark;
  error?: string;
}

export function Checkbox({ id, checked, onChange, label, className, fillStatus, error }: CheckboxProps) {
  return (
    <div className={cx("nv-check-wrap", error && "err")}>
      <label className={cx("nv-check", className)} htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-err` : undefined}
        />
        <span className="nv-check-box" aria-hidden="true">
          <CheckIcon size={13} />
        </span>
        <span className="nv-check-label">{label}</span>
        {fillStatus && <FillBadge mark={fillStatus} />}
      </label>
      {error && (
        <span className="nv-error" id={`${id}-err`}>
          <AlertIcon size={13} />
          <span>{error}</span>
        </span>
      )}
    </div>
  );
}
